import {
  getBaseFromPinyinNumber,
  getToneFromPinyinNumber,
  normalizePinyinNumber,
  tokenizePinyinNumber
} from './pinyin'

const initials = [
  'zh',
  'ch',
  'sh',
  'b',
  'p',
  'm',
  'f',
  'd',
  't',
  'n',
  'l',
  'g',
  'k',
  'h',
  'j',
  'q',
  'x',
  'r',
  'z',
  'c',
  's'
]

const zeroInitials = new Map([
  ['yi', 'i'],
  ['ya', 'ia'],
  ['ye', 'ie'],
  ['yao', 'iao'],
  ['you', 'iu'],
  ['yan', 'ian'],
  ['yang', 'iang'],
  ['yin', 'in'],
  ['ying', 'ing'],
  ['wu', 'u'],
  ['wa', 'ua'],
  ['wo', 'uo'],
  ['wai', 'uai'],
  ['wei', 'ui'],
  ['wan', 'uan'],
  ['wang', 'uang'],
  ['wen', 'un'],
  ['weng', 'ueng'],
  ['yu', 'ü'],
  ['yue', 'üe'],
  ['yuan', 'üan'],
  ['yun', 'ün'],
  ['yong', 'iong']
])

const confusionGroups: Array<{ tag: string; sounds: string[] }> = [
  { tag: 'confusion-jqx', sounds: ['initial-j', 'initial-q', 'initial-x'] },
  {
    tag: 'confusion-zhchshr',
    sounds: ['initial-zh', 'initial-ch', 'initial-sh', 'initial-r']
  },
  { tag: 'confusion-zcs', sounds: ['initial-z', 'initial-c', 'initial-s'] },
  { tag: 'confusion-an-ang', sounds: ['final-an', 'final-ang'] },
  { tag: 'confusion-en-eng', sounds: ['final-en', 'final-eng'] },
  { tag: 'confusion-u-ü', sounds: ['final-u', 'final-ü'] },
  { tag: 'confusion-tone-2-3', sounds: ['tone-2', 'tone-3'] }
]

export function getSyllableSoundTags(pinyinNumber: string) {
  const normalized = normalizePinyinNumber(pinyinNumber)
  const tone = getToneFromPinyinNumber(normalized)
  const base = getBaseFromPinyinNumber(normalized)
  const tags = new Set<string>([`tone-${tone}`, `syllable-${base}`])
  const { initial, final } = splitPinyinBase(base)

  tags.add(initial ? `initial-${initial}` : 'initial-zero')
  if (final) tags.add(`final-${final}`)
  if (['zhi', 'chi', 'shi', 'ri', 'zi', 'ci', 'si'].includes(base)) {
    tags.add('final-apical-i')
  }

  for (const group of confusionGroups) {
    if (group.sounds.some((tag) => tags.has(tag))) tags.add(group.tag)
  }

  return Array.from(tags)
}

export function getWordSoundTags(pinyinNumber: string, extra: string[] = []) {
  const tags = new Set(extra)
  for (const token of tokenizePinyinNumber(pinyinNumber)) {
    for (const tag of getSyllableSoundTags(token)) tags.add(tag)
  }
  return Array.from(tags).sort()
}

export function splitPinyinBase(base: string) {
  const zeroFinal = zeroInitials.get(base)
  if (zeroFinal) return { initial: '', final: zeroFinal }

  const initial = initials.find((candidate) => base.startsWith(candidate)) ?? ''
  if (!initial) return { initial: '', final: base }

  let final = base.slice(initial.length)
  if (['j', 'q', 'x'].includes(initial) && final.startsWith('u')) {
    final = `ü${final.slice(1)}`
  }

  return { initial, final }
}

export function getConfusionTags(soundTags: string[]) {
  return soundTags.filter((tag) => tag.startsWith('confusion-')).sort()
}

export const pronunciationInitials = initials

export const pronunciationFinals = [
  'a',
  'o',
  'e',
  'i',
  'u',
  'ü',
  'ai',
  'ei',
  'ao',
  'ou',
  'an',
  'ang',
  'en',
  'eng',
  'er',
  'ia',
  'ie',
  'iao',
  'iu',
  'ian',
  'iang',
  'in',
  'ing',
  'iong',
  'ua',
  'uo',
  'uai',
  'ui',
  'uan',
  'uang',
  'un',
  'ong',
  'üe',
  'üan',
  'ün'
]

export const zhuyinInitialsByPinyin: Record<string, string> = {
  b: 'ㄅ',
  p: 'ㄆ',
  m: 'ㄇ',
  f: 'ㄈ',
  d: 'ㄉ',
  t: 'ㄊ',
  n: 'ㄋ',
  l: 'ㄌ',
  g: 'ㄍ',
  k: 'ㄎ',
  h: 'ㄏ',
  j: 'ㄐ',
  q: 'ㄑ',
  x: 'ㄒ',
  zh: 'ㄓ',
  ch: 'ㄔ',
  sh: 'ㄕ',
  r: 'ㄖ',
  z: 'ㄗ',
  c: 'ㄘ',
  s: 'ㄙ'
}

export const zhuyinFinalsByPinyin: Record<string, string> = {
  a: 'ㄚ',
  o: 'ㄛ',
  e: 'ㄜ',
  i: 'ㄧ',
  u: 'ㄨ',
  ü: 'ㄩ',
  ai: 'ㄞ',
  ei: 'ㄟ',
  ao: 'ㄠ',
  ou: 'ㄡ',
  an: 'ㄢ',
  ang: 'ㄤ',
  en: 'ㄣ',
  eng: 'ㄥ',
  er: 'ㄦ',
  ia: 'ㄧㄚ',
  ie: 'ㄧㄝ',
  iao: 'ㄧㄠ',
  iu: 'ㄧㄡ',
  ian: 'ㄧㄢ',
  iang: 'ㄧㄤ',
  in: 'ㄧㄣ',
  ing: 'ㄧㄥ',
  iong: 'ㄩㄥ',
  ua: 'ㄨㄚ',
  uo: 'ㄨㄛ',
  uai: 'ㄨㄞ',
  ui: 'ㄨㄟ',
  uan: 'ㄨㄢ',
  uang: 'ㄨㄤ',
  un: 'ㄨㄣ',
  ong: 'ㄨㄥ',
  üe: 'ㄩㄝ',
  üan: 'ㄩㄢ',
  ün: 'ㄩㄣ'
}
